import {
  useAllParticipants,
  useConfigStore,
  useMainParticipant,
} from '@cometchat/calls-sdk-core';
import React, { useEffect, useRef, useState } from 'react';
import DraggableTile from '../draggable-tile/DraggableTile.web';
import Tile from '../tile/Tile.web';
import './Spotlight.css';

const Spotlight: React.FC = () => {
  const participants = useAllParticipants();
  const mainParticipant = useMainParticipant();
  const containerRef = useRef<HTMLDivElement>(null);
  const enableSpotlightDrag = useConfigStore(
    state => state.enableSpotlightDrag,
  );
  const [containerBounds, setContainerBounds] = useState<{
    width: number;
    height: number;
  }>({
    width: 0,
    height: 0,
  });

  // Update container bounds when component mounts or resizes
  useEffect(() => {
    const updateBounds = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const newBounds = {
          width: rect.width,
          height: rect.height,
        };

        // Only update if there's actually a change to avoid unnecessary re-renders
        setContainerBounds(prevBounds => {
          if (
            prevBounds.width !== newBounds.width ||
            prevBounds.height !== newBounds.height
          ) {
            console.log('Container bounds updated:', newBounds);
            return newBounds;
          }
          return prevBounds;
        });
      }
    };

    updateBounds();

    // Use ResizeObserver to monitor the spotlight container size changes
    let resizeObserver: ResizeObserver | null = null;

    if (containerRef.current) {
      resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          // Use contentRect which gives us the actual content box dimensions
          const { width, height } = entry.contentRect;
          const newBounds = { width, height };

          console.log('ResizeObserver triggered:', newBounds);

          setContainerBounds(prevBounds => {
            if (
              prevBounds.width !== newBounds.width ||
              prevBounds.height !== newBounds.height
            ) {
              return newBounds;
            }
            return prevBounds;
          });
        }
      });
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      if (resizeObserver && containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
        resizeObserver.disconnect();
      }
    };
  }, []);

  // Get the main participant (non-local) to display in fullscreen
  const spotlightParticipant =
    participants.find(p => !p.isLocal) || mainParticipant;
  return (
    <div ref={containerRef} className="cometchat-calls-spotlight-container">
      {/* Main participant in fullscreen */}
      <div className="cometchat-calls-spotlight-main-view">
        <Tile participant={spotlightParticipant} />
      </div>

      {/* Draggable local participant tile */}
      {participants.length > 1 &&
        containerBounds.width > 0 &&
        containerBounds.height > 0 && (
          <DraggableTile
            disableDrag={!enableSpotlightDrag}
            containerBounds={containerBounds}
          />
        )}
    </div>
  );
};

export default Spotlight;
